"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { SidebarNavDropdownItem, SidebarNavItem } from "./SidebarNav.types";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { ChevronRight } from "lucide-react";

type Props = {
  item: SidebarNavItem;
  collapsed: boolean;
  onNavigate: (item: SidebarNavItem | SidebarNavDropdownItem) => void;
};

const SidebarDropdownMenuItemButton = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      "flex h-8 w-full cursor-pointer items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm text-sidebar-foreground outline-none transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground data-[highlighted]:bg-sidebar-accent data-[highlighted]:text-sidebar-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50 ring-sidebar-ring focus-visible:ring-2 [&>span:last-child]:truncate [&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0",
      className,
    )}
    {...props}
  />
));

SidebarDropdownMenuItemButton.displayName = "SidebarDropdownMenuItemButton";

export const SidebarDropdownNavItem: React.FC<Props> = ({ item, collapsed, onNavigate }) => {
  const Icon = item.icon;
  const [open, setOpen] = useState(false);
  const closeTimeout = useRef<number | null>(null);
  const openOnHover = !collapsed;

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    clearCloseTimeout();
    closeTimeout.current = window.setTimeout(() => {
      setOpen(false);
      closeTimeout.current = null;
    }, 120);
  }, [clearCloseTimeout]);

  useEffect(() => {
    return () => clearCloseTimeout();
  }, [clearCloseTimeout]);

  const handleMouseEnter = useCallback(() => {
    if (!openOnHover) return;
    clearCloseTimeout();
    setOpen(true);
  }, [clearCloseTimeout, openOnHover]);

  const handleMouseLeave = useCallback(() => {
    if (!openOnHover) return;
    scheduleClose();
  }, [openOnHover, scheduleClose]);

  return (
    <SidebarMenuItem onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <DropdownMenu open={open} onOpenChange={(next) => (next ? setOpen(true) : setOpen(false))}>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton tooltip={item.label} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            {Icon ? <Icon className="h-5 w-5" /> : null}
            {!collapsed && <span>{item.label}</span>}
            {!collapsed && <ChevronRight className="ml-auto h-4 w-4" />}
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side={item.dropdownSide ?? "right"}
          align="start"
          sideOffset={0}
          className="w-auto min-w-[160px] max-w-[260px] p-2"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {item.dropdownItems?.map((dropdownItem) => {
            const DropdownIcon = dropdownItem.icon;
            return (
              <SidebarDropdownMenuItemButton key={dropdownItem.label} onSelect={() => onNavigate(dropdownItem)}>
                {DropdownIcon ? <DropdownIcon size={16} className="h-4 w-4 shrink-0" /> : null}
                <span className="leading-5">{dropdownItem.label}</span>
              </SidebarDropdownMenuItemButton>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
};
